import { useEffect, useState } from 'react';
import axios from 'axios';
import vopayApi from '@/services/api.config';
import { downloadBase64AsFile } from 'vopay';
import type {
  FileConversionRequest,
  FileConversionResponse,
  FileConversionHistoryRequest,
  FileConversionHistoryResponse,
  FileConversionHistoryItem,
  FileConversionDownloadRequest,
  FileConversionDownloadResponse,
  FileConversionSubmitRequest,
  FileConversionSubmitResponse,
  FileConversionDownloadSourceRequest,
  FileConversionDownloadSourceResponse,
  FileConversionUploadRequest,
  FileConversionUploadResponse,
} from 'vopay';

const { api } = vopayApi;

export default function useFileConversion() {
  const source = axios.CancelToken.source();
  const [loading, setLoading] = useState(false);
  const [convertedFile, setConvertedFile] = useState<string | null>(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [history, setHistory] = useState<FileConversionHistoryItem[]>([]);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);

  useEffect(() => {
    return () => {
      source.cancel();
    };
  }, []);

  async function convertFile(params: FileConversionRequest): Promise<FileConversionResponse> {
    try {
      setLoading(true);
      setConvertedFile(null);

      const res = await api.convertFile(params, source);

      if (!res.Success) {
        throw new Error(res.ErrorMessage);
      }

      setConvertedFile(res.TargetFile);
      return res;
    } catch (err) {
      console.error('File conversion failed:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }

  async function getHistory(params: FileConversionHistoryRequest = {}): Promise<FileConversionHistoryResponse> {
    try {
      setHistoryLoading(true);

      const res = await api.getFileConversionHistory(params, source);

      if (!res.Success) {
        throw new Error(res.ErrorMessage);
      }

      setHistory(res.Conversions.filter((item) => item.FileConversionID));

      return res;
    } catch (err) {
      setHistory([]);
      console.error('Failed to fetch file conversion history:', err);
      throw err;
    } finally {
      setHistoryLoading(false);
    }
  }

  async function downloadFile(params: FileConversionDownloadRequest): Promise<FileConversionDownloadResponse> {
    try {
      setDownloadLoading(true);

      const res = await api.downloadFileConversion(params, source);

      if (!res.Success) {
        throw new Error(res.ErrorMessage);
      }
      downloadBase64AsFile(res.FileContent, res.FileName);
      return res;
    } catch (err) {
      console.error('Failed to download file:', err);
      throw err;
    } finally {
      setDownloadLoading(false);
    }
  }

  async function downloadSourceFile(
    params: FileConversionDownloadSourceRequest,
  ): Promise<FileConversionDownloadSourceResponse> {
    try {
      setDownloadLoading(true);

      const res = await api.downloadSourceFileConversion(params, source);

      if (!res.Success) {
        throw new Error(res.ErrorMessage);
      }
      downloadBase64AsFile(res.FileContent, res.FileName);
      return res;
    } catch (err) {
      console.error('Failed to download file:', err);
      throw err;
    } finally {
      setDownloadLoading(false);
    }
  }

  async function uploadFile(params: FileConversionUploadRequest): Promise<FileConversionUploadResponse> {
    try {
      setSubmitLoading(true);

      const res = await api.uploadFileConversion(params, source);

      if (!res.Success) {
        throw new Error(res.ErrorMessage);
      }

      return res;
    } catch (err) {
      console.error('Failed to submit file:', err);
      throw err;
    } finally {
      setSubmitLoading(false);
    }
  }

  async function submitFile(params: FileConversionSubmitRequest): Promise<FileConversionSubmitResponse> {
    try {
      setSubmitLoading(true);

      const res = await api.submitFileConversion(params, source);

      if (!res.Success) {
        throw new Error(res.ErrorMessage);
      }

      return res;
    } catch (err) {
      console.error('Failed to submit file:', err);
      throw err;
    } finally {
      setSubmitLoading(false);
    }
  }

  return {
    loading,
    convertedFile,
    convertFile,
    historyLoading,
    history,
    getHistory,
    downloadLoading,
    downloadFile,
    downloadSourceFile,
    submitLoading,
    submitFile,
    uploadFile,
  };
}
